import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import OpenAI from 'openai';
import {
  ChatCompletionCreateParams,
  ChatCompletionMessageParam,
  ChatCompletionUserMessageParam,
} from 'openai/resources';
import {
  Observable,
  throwError,
  TimeoutError,
  BehaviorSubject,
  from,
} from 'rxjs';
import {
  timeout,
  catchError,
  retry,
  reduce,
  map,
  switchMap,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PromptingService } from './prompting.service';
import { ToastrService } from 'ngx-toastr';
import { CrashReportsService } from './crash-reports.service';

/**
 * Configuration constants for the AI service
 */
const AI_SERVICE_CONFIG = {
  DEFAULT_TIMEOUT: 60000, // 30 seconds
  STREAMING_TIMEOUT: 120000, // 120 seconds for streaming responses
  MAX_RETRIES: 0,
  RETRY_DELAY: 1000,
} as const;

/**
 * Interface for chat completion options
 */
interface ChatCompletionOptions {
  stream?: boolean;
  useTemplate?: boolean;
  timeoutMs?: number;
  temperature?: number;
  maxTokens?: number;
  model?: string;
  silentErrors?: boolean;
}

/**
 * Service for handling AI-powered chat completions and text generation
 * using OpenAI's API with enhanced error handling and timeout management.
 */
@Injectable({
  providedIn: 'root',
})
export class AiService {
  private readonly openai: OpenAI;
  private currentModel: string;
  private readonly systemPrompt: string;
  private isReadySubject = new BehaviorSubject<boolean>(false);
  public isReady$ = this.isReadySubject.asObservable();
  public hasInitializationError = false;
  private lastErrorTime = 0;
  private readonly ERROR_THROTTLE_MS = 3000;

  constructor(
    private readonly promptingService: PromptingService,
    private readonly toastr: ToastrService,
    private readonly crashReports: CrashReportsService,
  ) {
    this.openai = new OpenAI({
      apiKey: environment.aiConfig.apiKey,
      dangerouslyAllowBrowser: true,
      maxRetries: AI_SERVICE_CONFIG.MAX_RETRIES,
      baseURL: environment.aiConfig.aiBaseUrl,
      timeout: AI_SERVICE_CONFIG.DEFAULT_TIMEOUT,
    });

    this.currentModel = environment.aiConfig.modelName;
    this.systemPrompt = this.buildSystemPrompt();

    this.initializeModel();
  }

  /**
   * Initialize and verify the current model
   */
  private async initializeModel(): Promise<void> {
    try {
      const model = await this.getAvailableModel();
      if (model) {
        this.currentModel = model;
        this.isReadySubject.next(true);
      } else {
        this.hasInitializationError = true;
      }
    } catch (error) {
      console.error('Failed to initialize AI model:', error);
      this.hasInitializationError = true;
    }
  }

  /**
   * Build the system prompt with instructions
   */
  private buildSystemPrompt(): string {
    return `You are a helpful assistant.

# RULES
- Provide clear, concise, and accurate answers
- Respond in English
- Avoid using Chinese Unicode or pinyin characters
- Maintain a professional and helpful tone`;
  }

  /**
   * Retrieve the first available model from the API
   */
  private async getAvailableModel(): Promise<string | null> {
    try {
      const response = await this.openai.models.list();

      if (!response?.data || response.data.length === 0) {
        console.warn('No models available from API');
        return null;
      }

      const preferredModel = response.data.find(
        (model) => model.id === environment.aiConfig.modelName,
      );

      return preferredModel ? preferredModel.id : response.data[0].id;
    } catch (error) {
      console.error('Error fetching available models:', error);
      return null;
    }
  }

  /**
   * Get chat completion with conversation history
   *
   * @param history - Array of previous messages in the conversation
   * @param message - The current user message
   * @param options - Configuration options for the completion
   * @returns Observable stream of response text chunks
   */
  public getChatCompletion(
    history: ChatCompletionUserMessageParam[],
    message: string,
    options: ChatCompletionOptions = {},
  ): Observable<string> {
    const {
      stream = true,
      useTemplate = true,
      timeoutMs = stream
        ? AI_SERVICE_CONFIG.STREAMING_TIMEOUT
        : AI_SERVICE_CONFIG.DEFAULT_TIMEOUT,
      temperature,
      maxTokens,
    } = options;

    const processedMessage = useTemplate
      ? this.applyTemplate(message)
      : message;

    const requestBody: ChatCompletionCreateParams = {
      model: options.model || this.currentModel,
      messages: this.buildMessages(history, processedMessage),
      stream: true, // Always stream internally to avoid timeouts
      ...(temperature !== undefined && { temperature }),
      ...(maxTokens !== undefined && { max_tokens: maxTokens }),
    };

    // Use a stream-based timeout that resets on every chunk
    let stream$ = this.executeChatCompletionRequest(requestBody).pipe(
      timeout({
        each: timeoutMs,
        with: () => throwError(() => new TimeoutError()),
      }),
    );

    // If the user didn't want a stream, collect all chunks and emit as one
    if (!stream) {
      stream$ = stream$.pipe(
        reduce((acc, chunk) => acc + chunk, ''),
        map((fullText) => fullText.trim()),
      );
    }

    return stream$.pipe(
      catchError((error) => this.handleError(error, options.silentErrors)),
    );
  }

  /**
   * Get a simple completion without conversation history
   *
   * @param prompt - The prompt text
   * @param options - Configuration options for the completion
   * @returns Observable stream of response text
   */
  public getCompletion(
    prompt: string,
    options: ChatCompletionOptions = {},
  ): Observable<string> {
    return this.getChatCompletion([], prompt, options);
  }

  /**
   * Apply template formatting to the message (can be extended as needed)
   */
  private applyTemplate(message: string): string {
    // Add any custom template logic here
    return message.trim();
  }

  /**
   * Build the complete message array including system prompt
   */
  private buildMessages(
    history: ChatCompletionUserMessageParam[],
    currentMessage: string,
  ): ChatCompletionMessageParam[] {
    return [
      {
        role: 'system',
        content: this.systemPrompt,
      },
      ...history,
      {
        role: 'user',
        content: currentMessage,
      },
    ];
  }

  /**
   * Execute the chat completion request with internal streaming and cancellation
   *
   * @param body - The API request parameters
   */
  private executeChatCompletionRequest(
    body: ChatCompletionCreateParams,
  ): Observable<string> {
    return new Observable<string>((observer) => {
      const abortController = new AbortController();

      this.openai.chat.completions
        .create(body, { signal: abortController.signal })
        .then(async (response) => {
          await this.handleStreamingResponse(response, observer);
        })
        .catch((error: Error) => {
          if (error.name === 'AbortError') {
            return;
          }
          observer.error(error);
        });

      return () => {
        // Cancel the request if the observable is unsubscribed
        abortController.abort();
      };
    });
  }

  /**
   * Handle streaming response from OpenAI
   *
   * @param response - The stream response
   * @param observer - The observer to emit data to
   */
  private async handleStreamingResponse(
    response: any,
    observer: any,
  ): Promise<void> {
    try {
      const stream = response as AsyncIterable<any>;

      for await (const chunk of stream) {
        const content = chunk.choices[0]?.delta?.content;
        const finishReason = chunk.choices[0]?.finish_reason;

        if (content) {
          observer.next(content);
        }

        if (finishReason === 'stop') {
          observer.complete();
          return;
        }
      }

      observer.complete();
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      observer.error(error);
    }
  }

  /**
   * Centralized error handling with user notifications
   */
  private handleError(error: Error, silent = false): Observable<never> {
    console.error('AI Service Error:', error);

    // Always log to crash reports
    this.crashReports.silentGleapHttpErrorReport(error as any);

    if (silent) {
      return throwError(() => error);
    }

    // Throttling to prevent alert spam
    const now = Date.now();
    if (now - this.lastErrorTime < this.ERROR_THROTTLE_MS) {
      return throwError(() => error);
    }
    this.lastErrorTime = now;

    let errorTitle = 'AI Service Error';
    let errorMessage = 'An unexpected error occurred. Please try again.';
    let useToast = true;

    if (error instanceof TimeoutError) {
      errorTitle = 'Request Timeout';
      errorMessage =
        'The AI service took too long to respond. Please try again.';
    } else if (error.message?.includes('API key')) {
      errorTitle = 'Configuration Error';
      errorMessage =
        'There is an issue with the API configuration. Please contact support.';
      useToast = false; // Important enough to use Swal
    } else if (
      error.message?.includes('network') ||
      error.message?.includes('fetch')
    ) {
      errorTitle = 'Network Error';
      errorMessage =
        'Unable to connect to the AI service. Please check your connection.';
    } else if (error.message?.includes('rate limit')) {
      errorTitle = 'Rate Limit Exceeded';
      errorMessage =
        'Too many requests. Please wait a moment before trying again.';
    }

    if (useToast) {
      this.toastr.error(errorMessage, errorTitle, {
        timeOut: 5000,
        progressBar: true,
      });
    } else {
      this.promptingService.warning(errorTitle, errorMessage, 'default');
    }

    return throwError(() => error);
  }

  /**
   * Get the current model being used
   */
  public getCurrentModel(): string {
    return this.currentModel;
  }

  /**
   * Set a different model for completions
   */
  public setModel(modelId: string): void {
    this.currentModel = modelId;
  }
}
